import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DashboardBusProvider } from "@/lib/dashboardBus";
import {
  DASHBOARD_WIDGET_REGISTRY,
  DASHBOARD_SLOT_ORDER,
} from "@/lib/dashboardWidgets";
import DashboardGreeting from "@/components/DashboardGreeting";

/**
 * Dashboard — permission-driven, dynamically composed from
 * `GET /api/dashboard/widgets`. This page contains NO role checks; role→widget
 * mapping lives centrally in `backend/dashboard_permissions.py`.
 *
 * Rendering:
 *   - fetch the permitted widget list once on mount
 *   - group entries by slot in the backend-declared order
 *   - render each slot as a distinct row so widgets in the same slot sit
 *     side-by-side (grid columns adapt to widget count)
 *   - keys with no registered React component are silently skipped so the
 *     backend can announce a widget ahead of the frontend implementation
 */
export default function Dashboard() {
  const [widgets, setWidgets] = useState(null); // null = loading
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/dashboard/widgets");
        if (alive) setWidgets(r.data.widgets || []);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Could not load dashboard");
      }
    })();
    return () => { alive = false; };
  }, []);

  if (error) {
    return (
      <div data-testid="dashboard-page" className="p-6">
        <div className="rounded-md border border-status-error-bg bg-status-error-bg p-4 text-sm text-status-error-fg">
          {error}
        </div>
      </div>
    );
  }

  if (widgets === null) {
    return (
      <div data-testid="dashboard-page" className="p-6 text-sm text-ink-secondary">
        Loading dashboard…
      </div>
    );
  }

  // Group widgets by slot, preserving backend-declared order within each slot.
  const bySlot = DASHBOARD_SLOT_ORDER.reduce((acc, slot) => {
    acc[slot] = widgets.filter((w) => w.slot === slot);
    return acc;
  }, {});

  return (
    <DashboardBusProvider>
      <div data-testid="dashboard-page" className="space-y-3">
        <DashboardGreeting />
        {DASHBOARD_SLOT_ORDER.map((slot) => {
          const inSlot = bySlot[slot];
          if (!inSlot || inSlot.length === 0) return null;
          const rendered = inSlot
            .map((w) => {
              const Cmp = DASHBOARD_WIDGET_REGISTRY[w.key];
              if (!Cmp) return null; // registry gap — backend announced a widget the FE hasn't implemented yet
              return <Cmp key={w.key} />;
            })
            .filter(Boolean);
          if (rendered.length === 0) return null;
          const gridCols =
            rendered.length === 1
              ? "grid-cols-1"
              : rendered.length === 2
              ? "grid-cols-1 lg:grid-cols-2"
              : "grid-cols-1 lg:grid-cols-3";
          return (
            <div
              key={slot}
              data-testid={`dashboard-slot-${slot}`}
              className={`grid gap-3 ${gridCols}`}
            >
              {rendered}
            </div>
          );
        })}
      </div>
    </DashboardBusProvider>
  );
}
